import {
  CircularProgress,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import SubtitleLink from 'src/components/SubtitleLink';
import { getOperationForHome } from 'src/services/operation';
import { truncateText } from 'src/utils/string';

interface RecentOperation {
  hashId: string;
  market?: {
    name?: string | null;
  } | null;
  name: string;
  slug: string;
}

interface OperationsResponse {
  data: RecentOperation[];
}

const headers = ['Mercado', 'Operação'];

const formatValue = (value?: string | null): string =>
  value && value !== '-' ? value : '—';

export default function Operations() {
  const {
    data: operations,
    isError,
    isLoading,
  } = useQuery<OperationsResponse>('operationsHome', getOperationForHome, {
    retry: false,
  });
  const rows = operations?.data ?? [];

  return (
    <Paper elevation={0} sx={{ p: '16px 16px 8px 24px' }}>
      <SubtitleLink
        title="Operações recentes"
        url="/operacoes"
        labelLink="Ver todas as operações"
      />
      <Table
        size="small"
        aria-label="Operações recentes"
        sx={{ mt: 2, tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell
                key={header}
                sx={{
                  color: '#797979',
                  fontSize: '14px',
                  fontWeight: 600,
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 0.75,
                  whiteSpace: 'normal',
                  width: header === 'Operação' ? '70%' : '30%',
                }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                sx={{ px: 1, py: 3, textAlign: 'center' }}>
                <CircularProgress size={20} aria-label="Carregando operações" />
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                sx={{ px: 1, py: 3, textAlign: 'center' }}>
                Não foi possível carregar as operações.
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={headers.length}
                sx={{ px: 1, py: 3, textAlign: 'center' }}>
                Nenhuma operação encontrada.
              </TableCell>
            </TableRow>
          )}
          {rows.map((operation) => (
            <TableRow key={operation.hashId}>
              <TableCell
                sx={{
                  fontSize: '14px',
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 1,
                  width: '20%',
                }}>
                {formatValue(operation.market?.name)}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  overflowWrap: 'anywhere',
                  px: 0.5,
                  py: 1,
                  width: '80%',
                }}>
                <MuiLink
                  color="text.primary"
                  component={RouterLink}
                  to={`/operacao/${operation.market?.name?.toLowerCase()}/${operation.slug}/${operation.hashId}`}
                  title={operation.name}
                  underline="none">
                  {truncateText(operation.name, 45)}
                </MuiLink>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
